import { Box, Button, IconButton, Typography } from '@mui/material';
import { DragEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TableVirtuoso, TableVirtuosoHandle } from 'react-virtuoso';
import { TableCellCandidateType, TableRecognizedLabel } from 'entities/Files/types';
import { applyColumnPinPreferences } from 'utils/applyColumnPinPreferences';
import { ColumnPreference } from 'entities/Preferences/types';
import { ColorPalette } from 'shared/consts/colorPalette';
import { Icon } from 'shared/ui/Icon';
import { getCellSourceId } from 'components/file-editor-components/file-viewer/region-types';
import { CellTarget } from 'components/file-editor-components/table-fields/types';
import {
	cellStyle,
	ESTIMATED_ROW_HEIGHT,
	getCellMap,
	getRowIndexes,
	MAX_TABLE_HEIGHT,
} from 'components/file-editor-components/table-fields/utils';
import TableCell from 'components/file-editor-components/table-fields/TableCell';

type SingleTableProps = {
	table: TableRecognizedLabel;
	tableIndex: number;
	selectedSourceId?: string | null;
	onSelectSource?: (sourceId: string | null) => void;
	drawMode?: boolean;
	pendingCellTarget?: CellTarget | null;
	onCellTargetClick?: (target: CellTarget) => void;
	onCellContentChange?: (target: CellTarget, content: string) => void;
	onCellCandidateSelect?: (target: CellTarget, candidate: TableCellCandidateType) => void;
	onCellDelete?: (target: CellTarget) => void;
	onAddRow?: (tableIndex: number) => void;
	onDeleteRow?: (tableIndex: number, rowIndex: number) => void;
	onDeleteColumn?: (tableIndex: number, columnIndex: number) => void;
	onReorderColumns?: (tableIndex: number, columnOrder: number[]) => void;
	getColumnPinPreferences?: (tableLabel: string | undefined) => ColumnPreference[];
	onTogglePinColumn?: (tableIndex: number, columnIndex: number) => void;
};

const SingleTable = memo(function SingleTable({
	table,
	tableIndex,
	selectedSourceId,
	onSelectSource,
	drawMode,
	pendingCellTarget,
	onCellTargetClick,
	onCellContentChange,
	onCellCandidateSelect,
	onCellDelete,
	onAddRow,
	onDeleteRow,
	onDeleteColumn,
	onReorderColumns,
	getColumnPinPreferences,
	onTogglePinColumn,
}: SingleTableProps) {
	const { t } = useTranslation();
	const [columnOrder, setColumnOrder] = useState<number[]>([]);
	const [draggedOverIndex, setDraggedOverIndex] = useState<number | null>(null);
	const [tableHeight, setTableHeight] = useState<number | null>(null);
	const virtuosoRef = useRef<TableVirtuosoHandle>(null);

	useEffect(() => {
		setColumnOrder(table.columnsHeaders.map((_, columnIndex) => columnIndex));
	}, [table.columnsHeaders]);

	const rowIndexes = useMemo(() => getRowIndexes(table.cells), [table.cells]);
	const cellMap = useMemo(() => getCellMap(table.cells), [table.cells]);

	useEffect(() => {
		if (!selectedSourceId?.startsWith(`cell-${tableIndex}-`)) return;
		const [, , rowIndexPart] = selectedSourceId.split('-');
		const rowIndex = Number(rowIndexPart);
		const virtualIndex = rowIndexes.indexOf(rowIndex);
		if (virtualIndex === -1) return;

		virtuosoRef.current?.scrollToIndex({ index: virtualIndex, align: 'center', behavior: 'smooth' });

		const timeout = setTimeout(() => {
			document
				.querySelector(`[data-source-id="${CSS.escape(selectedSourceId)}"]`)
				?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
		}, 350);
		return () => clearTimeout(timeout);
	}, [selectedSourceId, tableIndex, rowIndexes]);

	const columnsWithMulti = useMemo(() => {
		const set = new Set<number>();
		table.cells.forEach((cell) => {
			if (cell.status === 'MULTI') set.add(cell.columnIndex);
		});
		return set;
	}, [table.cells]);

	const columnsWithPins = useMemo(
		() => applyColumnPinPreferences(table.columnsHeaders, getColumnPinPreferences?.(table.label)),
		[table.columnsHeaders, getColumnPinPreferences, table.label],
	);

	const displayColumnOrder = useMemo(() => {
		const pinned = columnOrder.filter((columnIndex) => columnsWithPins[columnIndex]?.isPinned);
		const unpinned = columnOrder.filter((columnIndex) => !columnsWithPins[columnIndex]?.isPinned);
		return [...pinned, ...unpinned];
	}, [columnOrder, columnsWithPins]);

	const columnWidthsRef = useRef<Record<number, number>>({});
	const [measureTick, setMeasureTick] = useState(0);

	useEffect(() => {
		setMeasureTick((tick) => tick + 1);
	}, [displayColumnOrder]);

	const pinnedLeftOffsets = useMemo(() => {
		const offsets = new Map<number, number>();
		let cumulative = 0;
		displayColumnOrder.forEach((columnIndex) => {
			if (!columnsWithPins[columnIndex]?.isPinned) return;
			offsets.set(columnIndex, cumulative);
			cumulative += columnWidthsRef.current[columnIndex] ?? 80;
		});
		return offsets;
	}, [displayColumnOrder, columnsWithPins, measureTick]);

	const handleDragStart = useCallback((event: DragEvent<HTMLTableCellElement>, index: number) => {
		event.dataTransfer.setData('text/plain', String(index));
	}, []);

	const handleDragOver = useCallback((event: DragEvent<HTMLTableCellElement>, index: number) => {
		event.preventDefault();
		setDraggedOverIndex(index);
	}, []);

	const handleDrop = useCallback(
		(event: DragEvent<HTMLTableCellElement>, targetIndex: number) => {
			event.preventDefault();
			const sourceIndex = Number(event.dataTransfer.getData('text/plain'));
			setDraggedOverIndex(null);
			if (Number.isNaN(sourceIndex) || sourceIndex === targetIndex) return;

			const next = [...displayColumnOrder];
			const [moved] = next.splice(sourceIndex, 1);
			next.splice(targetIndex, 0, moved);
			setColumnOrder(next);
			onReorderColumns?.(tableIndex, next);
		},
		[displayColumnOrder, tableIndex, onReorderColumns],
	);

	const height = Math.min(tableHeight ?? (rowIndexes.length + 1) * ESTIMATED_ROW_HEIGHT, MAX_TABLE_HEIGHT);
	const canEditContent = Boolean(onCellContentChange);
	const canPickCandidate = Boolean(onCellCandidateSelect);
	const hasSelectHandler = Boolean(onSelectSource);

	return (
		<Box
			sx={{
				'padding': 1,
				'& table': { width: '100%', borderCollapse: 'separate', borderSpacing: 0 },
			}}
		>
			{onAddRow && (
				<Box sx={{ display: 'flex', gap: 1, marginBottom: 1 }}>
					<Button size={'small'} variant={'outlined'} onClick={() => onAddRow(tableIndex)}>
						{t('file_editor.add_row')}
					</Button>
				</Box>
			)}
			<TableVirtuoso
				ref={virtuosoRef}
				style={{ height, overflowX: 'auto' }}
				totalListHeightChanged={setTableHeight}
				data={rowIndexes}
				computeItemKey={(_, rowIndex) => rowIndex}
				fixedHeaderContent={() => (
					<tr>
						{displayColumnOrder.map((columnIndex, index) => {
							const header = columnsWithPins[columnIndex];
							return (
								<th
									key={columnIndex}
									ref={(el) => {
										if (el)
											columnWidthsRef.current[columnIndex] =
												el.getBoundingClientRect().width;
									}}
									draggable
									onDragStart={(event) => handleDragStart(event, index)}
									onDragOver={(event) => handleDragOver(event, index)}
									onDrop={(event) => handleDrop(event, index)}
									onDragEnd={() => setDraggedOverIndex(null)}
									style={{
										...cellStyle,
										borderColor: columnsWithMulti.has(columnIndex)
											? ColorPalette.jungleGreenTransparent
											: header?.autocomplete
											? ColorPalette.violetRedTransparent
											: ColorPalette.zircon,
										background:
											draggedOverIndex === index
												? ColorPalette.periwinkle
												: ColorPalette.zircon,
										cursor: 'grab',
										textAlign: 'left',
										zIndex: 4,
										...(header?.isPinned && {
											position: 'sticky',
											left: pinnedLeftOffsets.get(columnIndex) ?? 0,
										}),
									}}
								>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											gap: 0.5,
										}}
									>
										<Typography variant={'body2'} color={ColorPalette.astronaut}>
											{header?.columnName}
										</Typography>
										<Box sx={{ display: 'flex', gap: 1 }}>
											{onTogglePinColumn && (
												<IconButton
													draggable={false}
													onMouseDown={(event) => event.stopPropagation()}
													onClick={(event) => {
														event.stopPropagation();
														onTogglePinColumn(tableIndex, columnIndex);
													}}
													size={'small'}
													sx={{ padding: '2px', flexShrink: 0 }}
												>
													<Icon
														id={header?.isPinned ? 'pined_fill' : 'pin'}
														color={
															header?.isPinned
																? ColorPalette.royalBlue
																: ColorPalette.baliHai
														}
														style={{ width: '12px', height: '12px' }}
													/>
												</IconButton>
											)}
											{onDeleteColumn && (
												<IconButton
													draggable={false}
													onMouseDown={(event) => event.stopPropagation()}
													onClick={(event) => {
														event.stopPropagation();
														onDeleteColumn(tableIndex, columnIndex);
													}}
													size={'small'}
													sx={{ padding: '2px', flexShrink: 0 }}
												>
													<Icon
														id={'close_round'}
														color={ColorPalette.violetRed}
														style={{ width: '12px', height: '12px' }}
													/>
												</IconButton>
											)}
										</Box>
									</Box>
								</th>
							);
						})}
						{onDeleteRow && (
							<th
								style={{
									...cellStyle,
									width: '32px',
									minWidth: '32px',
									background: ColorPalette.zircon,
									zIndex: 3,
								}}
							/>
						)}
					</tr>
				)}
				itemContent={(_, rowIndex) => (
					<>
						{displayColumnOrder.map((columnIndex) => {
							const isPendingTarget =
								pendingCellTarget?.tableIndex === tableIndex &&
								pendingCellTarget.rowIndex === rowIndex &&
								pendingCellTarget.columnIndex === columnIndex;
							const sourceId = getCellSourceId(tableIndex, rowIndex, columnIndex);
							return (
								<TableCell
									key={columnIndex}
									tableIndex={tableIndex}
									rowIndex={rowIndex}
									columnIndex={columnIndex}
									cell={cellMap.get(`${rowIndex}:${columnIndex}`)}
									isSelected={selectedSourceId === sourceId}
									isPendingTarget={isPendingTarget}
									canEditContent={canEditContent}
									drawMode={drawMode}
									canPickCandidate={canPickCandidate}
									hasSelectHandler={hasSelectHandler}
									isPinned={Boolean(columnsWithPins[columnIndex]?.isPinned)}
									pinnedLeftOffset={pinnedLeftOffsets.get(columnIndex)}
									onSelectSource={onSelectSource}
									onCellTargetClick={onCellTargetClick}
									onCellContentChange={onCellContentChange}
									onCellCandidateSelect={onCellCandidateSelect}
									onCellDelete={onCellDelete}
								/>
							);
						})}
						{onDeleteRow && (
							<Box component={'td'} sx={{ ...cellStyle, textAlign: 'center' }}>
								<IconButton
									onClick={() => onDeleteRow(tableIndex, rowIndex)}
									size={'small'}
									sx={{ padding: '2px' }}
								>
									<Icon
										id={'close_round'}
										color={ColorPalette.violetRed}
										style={{ width: '14px', height: '14px' }}
									/>
								</IconButton>
							</Box>
						)}
					</>
				)}
			/>
		</Box>
	);
});

export default SingleTable;
